import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { techSlug, TechChip } from "@/lib/tech";
import NewsCard from "@/components/NewsCard";
import JobCard from "@/components/JobCard";
import InsightCard from "@/components/InsightCard";
import SeoHead from "@/components/SeoHead";
import { Clock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND}${url}`;
  return url;
};

const SectionHeader = ({ kicker, title, count, to }) => (
  <div className="flex items-end justify-between border-b border-[#111827] pb-2 mt-12 mb-5">
    <div>
      <div className="dl-overline text-[#00A878]">{kicker}</div>
      <h2 className="font-display text-2xl text-[#111827] mt-1">{title} <span className="text-[#9ca3af] text-base font-mono-dl">({count})</span></h2>
    </div>
    {to && <Link to={to} className="text-[12px] underline-offset-4 hover:underline text-[#4B5563]">→</Link>}
  </div>
);

const Tech = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [allTechs, setAllTechs] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/tech/${slug}`, { params: { language: lang } })
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response && e.response.status === 404) setNotFound(true); });
  }, [slug, lang]);

  useEffect(() => {
    api.get("/technologies").then((r) => setAllTechs(r.data || [])).catch(() => {});
  }, []);

  if (notFound) return <Navigate to={`/news?q=${encodeURIComponent(slug.replace(/-/g, " "))}`} replace />;
  if (!data) return <div className="dl-container py-20 text-[#4B5563]">…</div>;

  const name = data.name;
  const total = (data.news?.length || 0) + (data.jobs?.length || 0) + (data.insights?.length || 0) + (data.tutorials?.length || 0);
  const canonical = `${BACKEND || ""}/tech/${slug}`;
  const pageTitle = `${name} — Supply Chain AI · Dataleo Radar`;
  const descBase = lang === "fr"
    ? `Toutes les actualités, offres d'emploi, analyses et tutoriels Dataleo autour de ${name} en Supply Chain et Operations.`
    : `All Dataleo news, jobs, analyses and tutorials around ${name} in Supply Chain and Operations.`;

  return (
    <div data-testid={`tech-page-${slug}`} className="dl-container py-14">
      <SeoHead
        lang={lang}
        path={`/tech/${slug}`}
        title={`${name} — Supply Chain AI`}
        description={descBase}
      />
      <div className="border-b border-[#111827] pb-4 mb-2">
        <div className="dl-overline text-[#00A878]">
          {lang === "fr" ? "Technologie" : "Technology"} · <Link to="/jobs" className="hover:underline">{lang === "fr" ? "Toutes les offres" : "All jobs"}</Link>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 text-[#111827]">{name}</h1>
        <p className="mt-3 text-[#4B5563] max-w-3xl">{descBase}</p>
        <div className="mt-3 text-[12px] font-mono-dl uppercase tracking-wider text-[#4B5563]">
          {total} {lang === "fr" ? "résultats" : "items"} · {data.news?.length || 0} news · {data.jobs?.length || 0} jobs · {data.insights?.length || 0} insights · {data.tutorials?.length || 0} tutorials
        </div>
      </div>

      {total === 0 && (
        <div data-testid="tech-empty" className="text-[#4B5563] py-16 text-center">
          {lang === "fr" ? `Aucun contenu pour ${name} pour le moment.` : `No content for ${name} yet.`}
        </div>
      )}

      {data.jobs?.length > 0 && (
        <section data-testid="tech-section-jobs">
          <SectionHeader
            kicker={lang === "fr" ? "Recrutements" : "Hiring"}
            title={lang === "fr" ? "Offres d'emploi" : "Jobs"}
            count={data.jobs.length}
            to="/jobs"
          />
          <div className="grid md:grid-cols-2 gap-6">
            {data.jobs.map((j, i) => <JobCard key={j.id} item={j} idx={i} />)}
          </div>
        </section>
      )}

      {data.news?.length > 0 && (
        <section data-testid="tech-section-news">
          <SectionHeader
            kicker={lang === "fr" ? "Actualités" : "News"}
            title="News"
            count={data.news.length}
            to="/news"
          />
          <div className="grid md:grid-cols-2 gap-6">
            {data.news.map((n, i) => <NewsCard key={n.id} item={n} idx={i} />)}
          </div>
        </section>
      )}

      {data.insights?.length > 0 && (
        <section data-testid="tech-section-insights">
          <SectionHeader
            kicker="Editorial"
            title="Insights"
            count={data.insights.length}
            to="/insights"
          />
          <div className="grid md:grid-cols-2 gap-6">
            {data.insights.map((it, i) => <InsightCard key={it.id} item={it} idx={i} />)}
          </div>
        </section>
      )}

      {data.tutorials?.length > 0 && (
        <section data-testid="tech-section-tutorials">
          <SectionHeader
            kicker="How-to"
            title="Tutorials"
            count={data.tutorials.length}
            to="/tutorials"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.tutorials.map((tu, i) => (
              <Link
                key={tu.id}
                to={`/tutorials/${tu.id}`}
                data-testid={`tech-tutorial-card-${i}`}
                className="group bg-white border border-[#E5E7EB] hover:border-[#111827] transition-colors flex flex-col overflow-hidden"
              >
                {tu.featured_image && (
                  <div className="aspect-[16/9] bg-[#F3F4F6] overflow-hidden">
                    <img src={resolveImg(tu.featured_image)} alt={tu.title || "Tutorial cover"} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                )}
                <div className="p-4 flex flex-col gap-2 flex-1">
                  <div className="flex items-center gap-3 text-[10px] font-mono-dl tracking-wider uppercase text-[#4B5563]">
                    <span className="text-[#00A878]">{tu.difficulty}</span>
                    {tu.duration_minutes > 0 && <span className="inline-flex items-center gap-1"><Clock size={11} /> {tu.duration_minutes} min</span>}
                    <span className="ml-auto text-[#9ca3af]">{tu.published_at}</span>
                  </div>
                  <h3 className="font-display text-lg leading-snug text-[#111827] group-hover:text-[#00A878] transition-colors">{tu.title}</h3>
                  {tu.subtitle && <p className="text-[13px] text-[#4B5563] line-clamp-2">{tu.subtitle}</p>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Cross-linking to other techs for SEO discovery */}
      {allTechs.length > 1 && (
        <div className="mt-16 border-t border-[#E5E7EB] pt-8">
          <div className="dl-overline text-[#00A878] mb-3">
            {lang === "fr" ? "Autres technologies" : "Other technologies"}
          </div>
          <div className="flex flex-wrap gap-2">
            {allTechs.filter((x) => techSlug(x) !== slug).map((x) => (
              <TechChip key={x} name={x} variant="outline" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tech;
